// Pure outbox / sync helpers. Only type imports here, so the logic is unit-testable without a browser.
import type {
  AttendanceOutboxEntry,
  AttendanceWriteInput,
  ClassifiedSyncError,
  OfflineIdentity,
  OutboxEntry,
  SessionSummaryOutboxEntry,
  SessionSummaryWriteInput,
  SummaryCall,
} from './types';

export const ATTENDANCE_CHUNK_SIZE = 200;
export const BACKOFF_BASE_MS = 2_000;
export const BACKOFF_MAX_MS = 5 * 60_000;

export const attendanceKey = (enrollmentId: string) => `att:${enrollmentId}` as const;
export const sessionSummaryKey = (sessionId: string) => `sum:${sessionId}` as const;

/** Write timestamp / version of an entry (ISO). Strictly increases with every write of the same key. */
export function entryVersion(entry: OutboxEntry): string {
  return entry.kind === 'attendance' ? entry.markedAt : entry.updatedAt;
}

/**
 * Fresh timestamp for a new write. Guaranteed to be later than the previous write of the same key,
 * so last-write-wins on the server never treats a newer local change as stale (clock adjustments, same-ms taps).
 */
export function nextWriteTimestamp(nowMs: number, previous?: string): string {
  const prevMs = previous ? Date.parse(previous) : NaN;
  const ms = Number.isFinite(prevMs) && prevMs >= nowMs ? prevMs + 1 : nowMs;
  return new Date(ms).toISOString();
}

/** Builds / coalesces an attendance entry: the last tap wins, state is reset to pending. */
export function mergeAttendanceEntry(
  existing: AttendanceOutboxEntry | undefined,
  input: AttendanceWriteInput,
  identity: OfflineIdentity,
  nowMs: number
): AttendanceOutboxEntry {
  const markedAt = nextWriteTimestamp(nowMs, existing?.markedAt);
  return {
    kind: 'attendance',
    key: attendanceKey(input.enrollmentId),
    schoolId: input.schoolId,
    userId: identity.userId,
    activeRole: identity.activeRole,
    sessionId: input.sessionId,
    enrollmentId: input.enrollmentId,
    studentMemberId: input.studentMemberId ?? existing?.studentMemberId,
    status: input.status,
    markedAt,
    state: 'pending',
    attempts: 0,
    createdAt: existing?.createdAt ?? new Date(nowMs).toISOString(),
  };
}

/**
 * Builds / coalesces a session summary entry: provided fields overwrite, missing fields are kept,
 * `complete` stays true once set. Every write gets a fresh `updatedAt`.
 */
export function mergeSessionSummaryEntry(
  existing: SessionSummaryOutboxEntry | undefined,
  input: SessionSummaryWriteInput,
  identity: OfflineIdentity,
  nowMs: number
): SessionSummaryOutboxEntry {
  const updatedAt = nextWriteTimestamp(nowMs, existing?.updatedAt);
  return {
    kind: 'sessionSummary',
    key: sessionSummaryKey(input.sessionId),
    schoolId: input.schoolId,
    userId: identity.userId,
    activeRole: identity.activeRole,
    sessionId: input.sessionId,
    notes: input.notes !== undefined ? input.notes : existing?.notes,
    topic: input.topic !== undefined ? input.topic : existing?.topic,
    title: input.title !== undefined ? input.title : existing?.title,
    complete: existing?.complete || input.complete ? true : undefined,
    updatedAt,
    state: 'pending',
    attempts: 0,
    createdAt: existing?.createdAt ?? new Date(nowMs).toISOString(),
  };
}

/**
 * The calls needed to sync one summary entry. All of them carry the SAME `clientUpdatedAt` (the entry's
 * `updatedAt`) because notes and topic share one server timestamp (`SummaryUpdatedAt`). A completed entry is
 * sent as a single summarize call (it sets notes + topic + title at once).
 */
export function buildSummaryCalls(entry: SessionSummaryOutboxEntry): SummaryCall[] {
  const clientUpdatedAt = entry.updatedAt;
  if (entry.complete) {
    return [
      {
        type: 'summarize',
        body: { notes: entry.notes ?? '', topic: entry.topic ?? '', title: entry.title, clientUpdatedAt },
      },
    ];
  }
  const calls: SummaryCall[] = [];
  if (entry.notes !== undefined) calls.push({ type: 'notes', body: { notes: entry.notes, clientUpdatedAt } });
  if (entry.topic !== undefined) calls.push({ type: 'topic', body: { topic: entry.topic, clientUpdatedAt } });
  return calls;
}

/** Exponential backoff: 2 s, 4 s, 8 s … capped at 5 min. `attempts` is the number of failures so far (≥ 1). */
export function backoffDelayMs(attempts: number): number {
  const exp = Math.max(0, attempts - 1);
  return Math.min(BACKOFF_MAX_MS, BACKOFF_BASE_MS * 2 ** Math.min(exp, 20));
}

export function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

interface ErrorLike {
  message?: string;
  code?: string;
  response?: { status?: number; data?: { code?: string; Code?: string; detail?: string; Detail?: string } | unknown };
}

/**
 * Maps a request error to how the outbox reacts:
 * - `network`  — no response (offline / DNS / CORS / timeout): keep pending, back off, stop this flush.
 * - `transient` — 5xx, 408, 429: keep pending, back off.
 * - `auth`     — 401: keep pending, try a silent token renew.
 * - `rejected` — other 4xx: business error; keep the entry with `state: 'rejected'` and the error code.
 */
export function classifySyncError(error: unknown): ClassifiedSyncError {
  const err = (error ?? {}) as ErrorLike;
  const status = err.response?.status;
  if (!err.response || status === undefined || status === 0) {
    return { kind: 'network', code: 'network_error', message: err.message };
  }
  const data = (err.response.data ?? {}) as { code?: string; Code?: string; detail?: string; Detail?: string };
  const message = data.detail ?? data.Detail ?? err.message;
  if (status === 401) return { kind: 'auth', code: 'unauthorized', message };
  if (status >= 500 || status === 408 || status === 429) return { kind: 'transient', code: `http_${status}`, message };
  const code = data.code ?? data.Code ?? (status === 403 ? 'forbidden' : status === 404 ? 'not_found' : `http_${status}`);
  return { kind: 'rejected', code, message };
}

/** Storage key of an entry: entries of different users on the same device never collide. */
export function storageKeyOf(userId: string, key: string): string {
  return `${userId}|${key}`;
}
